import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api";
import type { Principal } from "./types";

interface AuthContextValue {
  principal: Principal | null;
  loading: boolean;
  refresh: () => Promise<void>;
  /** `turnstileToken` is required by the server in production (see components/Turnstile). */
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setPrincipal(await api.get<Principal>("/api/me"));
    } catch {
      setPrincipal(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string, turnstileToken?: string) => {
      await api.post("/auth/sign-in/email", { email, password, turnstileToken });
      await refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/sign-out", {});
    } catch {
      /* ignore */
    }
    setPrincipal(null);
  }, []);

  return <AuthContext.Provider value={{ principal, loading, refresh, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function hasPermission(principal: Principal | null, permission: string): boolean {
  return principal?.permissions.includes(permission) ?? false;
}
